class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.modal = this.closest('.search-modal');
    this.cachedResults = {};
    this.isOpen = false;
    this.searchTerm = '';
  }

  connectedCallback() {
    // Find elements
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    
    if (!this.input || !this.predictiveSearchResults) {
      // Try to find elements again after a short delay
      setTimeout(() => {
        this.input = this.querySelector('input[type="search"]');
        this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
        if (this.input && this.predictiveSearchResults) {
          this.setupEventListeners();
        }
      }, 100);
      return;
    }

    // Save default markup so we can restore it when the search is cleared
    if (this.predictiveSearchResults) {
      this.defaultMarkup = this.predictiveSearchResults.innerHTML;
    }

    this.setupEventListeners();
  }

  setupEventListeners() {
    const form = this.querySelector('form.search');
    if (form) {
      form.addEventListener('submit', this.onFormSubmit.bind(this));
    }
    // Only bind the close handler to an explicit modal-close button (if present).
    // Avoid selecting the first generic button[type="button"] which may be
    // the header clear/close icon — that should not close the predictive search.
    const closeButton = this.querySelector('.search-modal__close-button');
    if (closeButton) {
      closeButton.addEventListener('click', this.close.bind(this));
    }
    const resetButton = this.querySelector('button[type="reset"]');
    if (resetButton) {
      resetButton.addEventListener('click', this.clear.bind(this));
    }

    // Attach clear button(s) used in header/modal markup
    const clearButton = this.querySelector('.header-search-clear-button');
    if (clearButton) {
      clearButton.addEventListener('click', (event) => {
        event.preventDefault();
        // On clear icon click, clear text and show default results (don't close popup)
        this.showDefaultResults();
      });
    }

    const clearTextButton = this.querySelector('.header-search-clear-text');
    if (clearTextButton) {
      clearTextButton.addEventListener('click', (event) => {
        event.preventDefault();
        this.showDefaultResults();
      });
    }

    if (this.input) {
      this.input.addEventListener('input', debounce((event) => {
        this.onChange(event);
      }, 300).bind(this));
      this.input.addEventListener('focus', this.onFocus.bind(this));
    }

    // Header search field element (used to toggle active border state)
    this.headerField = this.querySelector('.header-search-field');

    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input ? this.input.value.trim() : '';
  }

  onChange() {
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    // Toggle active state for the header search field to control border color and clear button visibility
    if (this.headerField) {
      if (this.searchTerm.length) this.headerField.classList.add('is-active');
      else this.headerField.classList.remove('is-active');
    }

    if (!this.searchTerm.length) {
      // When input is cleared, restore default results and keep popup open
      this.showDefaultResults();
      return;
    }

    this.getSearchResults(this.searchTerm);
  }
  
  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFocus() {
    document.body.classList.add('predictive-search--focus');
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.headerField) this.headerField.classList.add('is-active');

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  } 

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    }, 100);
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      this.showDefaultResults();
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
     
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      /*
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      */
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allElements = this.querySelectorAll('li');
    let activeElement = this.querySelector('li');

    if (moveUp && !selectedElement) return;

    this.statusElement.textContent = ''; 

    if (!moveUp && selectedElement) {
      activeElement = selectedElement.nextElementSibling || allElements[0];
    } else if (moveUp) {
      activeElement = selectedElement.previousElementSibling || allElements[allElements.length - 1];
    }

    if (activeElement === selectedElement) return;

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
 
    this.setLiveRegionText(activeElement.textContent);
    if (this.input) {
      this.input.setAttribute('aria-activedescendant', activeElement.id);
    }
  }

  selectOption() {
    const selectedProduct = this.querySelector('[aria-selected="true"] a, [aria-selected="true"] button');

    if (selectedProduct) selectedProduct.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(" ", "-").toLowerCase();
    const perPage = this.dataset.perPage || 4;
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      if (this.modal) this.modal.classList.add('searching');
      return;
    }

    fetch(`${theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&${encodeURIComponent('resources[limit]')}=${perPage}&${encodeURIComponent('resources[limit_scope]')}=each&section_id=predictive-search`)
      .then((response) => { 
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search').innerHTML;

        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach(
          (predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          }
        );
        /*
        this.cachedResults[queryKey] = resultsMarkup;
        */
        this.renderSearchResults(resultsMarkup);
        
        if (this.modal) this.modal.classList.add('searching');
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        
        this.close();
        throw error;
      }); 
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);  

    // Add "Categories" header for search modal - use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.addCategoriesHeader();
        this.setupSearchForButton();
        // Bold matched terms in collections/pages/products to match header behavior
        try {
          const term = this.getQuery();
          // Normalize any server-side <mark> into <strong> first so styling
          // for <strong> can be applied consistently, then apply client-side
          // highlighting and convert those marks to <strong> as well.
          this.replaceMarksWithStrong();
          if (term && term.length) {
            this.boldMatchesInContainer(term);
            this.replaceMarksWithStrong();
          }
        } catch (e) {
          // swallow
        }
      }, 0);
    });

    this.setLiveRegionResults();
    this.open();
  }

  // Replace any <mark> elements inside the predictive search results with
  // <strong> elements to keep highlighting markup consistent across devices.
  replaceMarksWithStrong() {
    if (!this.predictiveSearchResults) return;
    const marks = this.predictiveSearchResults.querySelectorAll('mark');
    marks.forEach(mark => {
      const strong = document.createElement('strong');
      // Preserve children and text
      while (mark.firstChild) strong.appendChild(mark.firstChild);
      // Copy attributes except style/background to avoid inline highlight bg
      for (let i = 0; i < mark.attributes.length; i++) {
        const attr = mark.attributes[i];
        if (attr.name !== 'style') strong.setAttribute(attr.name, attr.value);
      }
      mark.parentNode.replaceChild(strong, mark);
    });
  }

  // Adjust generated <strong> elements so they visually match desktop:
  // - inherit font-family
  // - set font-weight to parent's weight if >=500, otherwise use 500
  normalizeStrongWeights() {
    if (!this.predictiveSearchResults) return;
    const strongs = this.predictiveSearchResults.querySelectorAll('strong');
    strongs.forEach(el => {
      // left intentionally blank - this function is deprecated but kept
      // to avoid breaking older code if referenced elsewhere.
    });
  }

  // Simple text-node based highlighter used after render
  boldMatchesInContainer(searchTerm) {
    if (!searchTerm) return;
    const escaped = searchTerm.replace(/[.*+?^${}()|[\\]\\]/g, '\\$&');
    const regex = new RegExp(escaped, 'gi');

    const addBoldForSelector = (selector) => {
      const nodes = this.querySelectorAll(selector);
      nodes.forEach(element => {
        const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT, null, false);
        const textNodes = [];
        let node;
        while ((node = walker.nextNode())) textNodes.push(node);
        textNodes.forEach(textNode => {
          const str = textNode.nodeValue;
          if (!str || !regex.test(str)) return;
          regex.lastIndex = 0;
          const frag = document.createDocumentFragment();
          let lastIndex = 0;
          let match;
          while ((match = regex.exec(str)) !== null) {
            const idx = match.index;
            if (idx > lastIndex) frag.appendChild(document.createTextNode(str.slice(lastIndex, idx)));
            const mark = document.createElement('mark');
            mark.textContent = match[0];
            frag.appendChild(mark);
            lastIndex = regex.lastIndex;
          }
          if (lastIndex < str.length) frag.appendChild(document.createTextNode(str.slice(lastIndex)));
          textNode.parentNode.replaceChild(frag, textNode);
        });
      });
    };

    // Collections
    addBoldForSelector('#predictive-search-results-queries-list li[id*="collection"] .caption-regular p');
    // Pages
    addBoldForSelector('#predictive-search-results-pages-list-desktop .caption-regular p');
    addBoldForSelector('#predictive-search-results-pages-list-mobile .caption-regular p');
    // Products
    addBoldForSelector('#predictive-search-results-products-list .predictive-search__item-heading');
    addBoldForSelector('#predictive-search-results-products-list-mobile .predictive-search__item-heading');
    addBoldForSelector('.predictive-search__item-heading');
  }

  setupSearchForButton() {
    const searchForButton = this.querySelector('#predictive-search-option-search-keywords button');
    if (!searchForButton) {
      return;
    }

    // Remove existing listeners by cloning the button
    const newButton = searchForButton.cloneNode(true);
    searchForButton.parentNode.replaceChild(newButton, searchForButton);

    // Add click handler to navigate to search page
    newButton.addEventListener('click', (event) => {
      event.preventDefault();
      const searchTerm = this.getQuery();
      if (!searchTerm || !theme || !theme.routes || !theme.routes.search_url) {
        return;
      }
      
      // Navigate to search page with query parameter
      window.location.href = `${theme.routes.search_url}?q=${encodeURIComponent(searchTerm)}`;
    });
  }

  addCategoriesHeader() {
    // Only for search modal
    const searchModal = this.closest('.search-modal');
    if (!searchModal) {
      return;
    }

    const queriesList = this.querySelector('#predictive-search-results-queries-list');
    if (!queriesList) {
      return;
    }

    // Check if there are any collection items
    const firstCollectionItem = queriesList.querySelector('li[id*="collection"]');
    if (!firstCollectionItem) {
      return;
    }

    // Check if header already exists
    const listParent = queriesList.parentElement;
    if (!listParent) {
      return;
    }

    const existingHeader = listParent.querySelector('.predictive-search__categories-header');
    if (existingHeader) {
      return;
    }

    // Create header element
    const header = document.createElement(queriesList.closest('predictive-search')?.dataset.headingTag || 'h2');
    const categoriesStringTranslated = window.theme?.translations?.categories ?? 'Categories';
    const trendingNowStringTranslated = window.theme?.translations?.trendingNow ?? 'Trending now';

    header.className = 'predictive-search__categories-header heading-4';
    header.textContent = (this.input && this.input.value && this.input.value.trim().length) ? categoriesStringTranslated : trendingNowStringTranslated;

    // Insert header before the list
    listParent.insertBefore(header, queriesList);
  }

  // Static method that can be called from anywhere
  static addCategoriesHeaderStatic(container) {
    if (!container) {
      return;
    }

    const searchModal = container.closest('.search-modal');
    if (!searchModal) {
      return;
    }

    const queriesList = container.querySelector('#predictive-search-results-queries-list');
    if (!queriesList) {
      return;
    }

    // Check if there are any collection items
    const firstCollectionItem = queriesList.querySelector('li[id*="collection"]');
    if (!firstCollectionItem) {
      return;
    }

    // Check if header already exists
    const listParent = queriesList.parentElement;
    if (!listParent) {
      return;
    }

    const existingHeader = listParent.querySelector('.predictive-search__categories-header');
    if (existingHeader) {
      return;
    }

    // Create header element
    const header = document.createElement(queriesList.closest('predictive-search')?.dataset.headingTag || 'h2');
    header.className = 'predictive-search__categories-header heading-4';
    const categoriesStringTranslated = window.theme?.translations?.categories ?? 'Categories';
    const trendingNowStringTranslated = window.theme?.translations?.trendingNow ?? 'Trending now';

    // If container has an input with a value, treat as search results (Categories), otherwise default Trending now
    const containerInput = container.closest && container.closest('predictive-search') ? container.closest('predictive-search').querySelector('input[type="search"]') : container.querySelector('input[type="search"]');
    header.textContent = (containerInput && containerInput.value && containerInput.value.trim().length) ? categoriesStringTranslated : trendingNowStringTranslated;

    // Insert header before the list
    listParent.insertBefore(header, queriesList);
  }

  setLiveRegionResults() { 
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }
  
  getResultsMaxHeight() {
    this.resultsMaxHeight = window.innerHeight - document.querySelector('.header').getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    }
    this.setAttribute('open', true);
    if (this.input) {
      this.input.setAttribute('aria-expanded', true);
    }
    this.isOpen = true;
    document.body.classList.add('predictive-search--focus');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm && this.input) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    if (this.input) {
      this.input.setAttribute('aria-activedescendant', '');
      this.input.setAttribute('aria-expanded', false);
    }
    this.removeAttribute('loading');
    this.removeAttribute('open');
    document.body.classList.remove('predictive-search--focus');
  }

  clear(event) {
    if (event) {
      event.preventDefault();
    }
    
    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    // Restore default results when clearing
    this.showDefaultResults();
  }

  showDefaultResults() {
    if (this.predictiveSearchResults && this.defaultMarkup) {
      this.predictiveSearchResults.innerHTML = this.defaultMarkup;
      this.setAttribute('results', true);
      // Add categories header and search-for button wiring if available
      requestAnimationFrame(() => {
        setTimeout(() => {
          const topProductsStringTranslated = window.theme?.translations?.topProducts ?? 'Top products';
          const trendingNowStringTranslated = window.theme?.translations?.trendingNow ?? 'Trending now';

          // Ensure headings read "Trending now" / "Top products" for default state
          const categoriesHeader = this.querySelector('.predictive-search__categories-header');
          if (categoriesHeader) categoriesHeader.textContent = trendingNowStringTranslated;
          const productsHeading = this.querySelector('#predictive-search-products');
          if (productsHeading) productsHeading.textContent = topProductsStringTranslated;
          this.setupSearchForButton();
        }, 0);
      });
          // Normalize any server-side <mark> to <strong> before opening
          this.replaceMarksWithStrong();
          this.open();
    } else {
      // Fallback: remove results attribute and close
      this.removeAttribute('results');
      this.close();
    }
    if (this.modal) this.modal.classList.remove('searching');
  }
}

customElements.define('predictive-search', PredictiveSearch);
